import { SalvCheckbox, SalvSwitch } from '@salvapp/ui-components';
import React, { useCallback, useContext } from 'react';
import './ListSelector.scss';
import { useQueryCustomListScreeningUsableFields } from '../../../custom-lists/usableFields/customListScreeningUsableFieldsActions';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useDispatch } from 'react-redux';

interface Props {
  title: string;
  isSelected: boolean;
  onChange: (isToggled: boolean) => void;
  listId: string;
}

export const CustomListSelector = ({ title, isSelected, onChange, listId }: Props) => {
  const { customListConfiguration, updateCustomListConfiguration } = useContext(ScreenableFieldSetupContext);
  const dispatch = useDispatch();

  const toggleField = useCallback(
    (fieldId: string, fieldPath: string) => (isSelected: boolean) => {
      const values = [...customListConfiguration];
      const listConfigurationIndex = customListConfiguration.findIndex((it) => it.list.id === listId)!;
      if (isSelected) {
        values.splice(listConfigurationIndex, 1, {
          ...values[listConfigurationIndex],
          fields: [...values[listConfigurationIndex].fields, { id: fieldId, name: fieldPath }],
        });
        updateCustomListConfiguration(values);
      } else {
        values.splice(listConfigurationIndex, 1, {
          ...values[listConfigurationIndex],
          fields: [...values[listConfigurationIndex].fields.filter((field) => field.id !== fieldId)],
        });
        updateCustomListConfiguration(values);
      }
    },
    [listId, updateCustomListConfiguration]
  );

  const {
    data: screeningFields,
    isPending: isScreeningFieldsPending,
    isError: isScreeningFieldsError,
  } = useQueryCustomListScreeningUsableFields(listId);

  if (isScreeningFieldsError) {
    dispatch(
      showFeedback({
        message: 'Could not load Custom List Screening fields',
        success: false,
      })
    );
    return <div />;
  }

  return (
    !isScreeningFieldsPending && (
      <div className='list-selector m-t-8'>
        <div className='switch'>
          <div className='title-s'>{title}</div>
          <SalvSwitch ariaLabel={title} selected={isSelected} onChange={onChange} />
        </div>
        {isSelected && (
          <div className='fields'>
            {screeningFields.map((field) => (
              <SalvCheckbox
                key={field.id}
                label={field.name}
                selected={customListConfiguration
                  .find((it) => it.list.id === listId)
                  ?.fields.map((it) => it.id)
                  ?.includes(field.id!)}
                onChange={toggleField(field.id!, field.path)}
              />
            ))}
            {!screeningFields?.length && <div className='no-fields'>No fields configured</div>}
          </div>
        )}
      </div>
    )
  );
};
